import React from 'react';
import proptypes from 'prop-types';
import classnames from 'classnames/bind';
import { LayoutSection } from './LayoutSection';

const {
  string, shape, arrayOf, element, bool, oneOf
} = proptypes;

export const Layout = ({
  layoutType,
  components,
  layoutComponents = [],
  componentSelectorData,
  loading = { server: false, client: false },
  LoadingLayout = null,
  alwaysHydrate = false,
  enableB2CPaddingAndGap = false
}) => {
  const heroCarouselIdx = layoutComponents?.findIndex(
    (component) => component?.customRenderId === 'Hero Carousel'
  );
  const heroCarousel = layoutComponents[heroCarouselIdx];

  const marketingBannerIdx = layoutComponents?.findIndex(
    (component) => component?.customRenderId === 'internalMarketingBanner'
  );
  const marketingBanner = layoutComponents[marketingBannerIdx];

  const bentoHeroIdx = layoutComponents?.findIndex(
    (component) => component?.customRenderId === 'bentoHero'
  );
  const bentoHero = layoutComponents[bentoHeroIdx];

  const quickLinksIdx = layoutComponents?.findIndex(
    (component) => component?.customRenderId === 'quickLinks'
  );
  const quickLinks = layoutComponents[quickLinksIdx];

  const style = {
    layoutRoot: classnames(
      'sui-w-full',
      'sui-max-w-screen-2xl',
      'sui-m-auto',
      {
        'lg:sui-pb-12': layoutType === 'themed-layout',
        'sui-pb-8': layoutType === 'themed-layout',
      }
    ),
    layoutGrid: classnames(
      'sui-grid sui-grid-flow-row-dense sui-grid-cols-12',
      'sui-gap-4 sui-mx-4',
      { 'lg:sui-gap-y-12': layoutType === 'themed-layout',
        'md:sui-mx-6 xl:sui-mx-8 md:sui-gap-6 xl:sui-gap-8 sui-mt-4 md:sui-mt-6 xl:sui-mt-8': enableB2CPaddingAndGap,
        'lg:sui-mx-0': !enableB2CPaddingAndGap, },
    )
  };

  const componentOrderComponents = () => (
    layoutComponents.map((layoutComponent, index) => {
      if (
        !layoutComponent
            || !layoutComponent?.customRenderId
            || layoutComponent?.customRenderId === 'Hero Carousel'
            || layoutComponent?.customRenderId === 'internalMarketingBanner'
            || layoutComponent?.customRenderId === 'bentoHero'
            || layoutComponent?.customRenderId === 'quickLinks'
      ) return false;

      return (
        <LayoutSection
          key={index}
          layoutComponent={layoutComponent}
          index={index}
          components={components}
          componentSelectorData={componentSelectorData}
          alwaysHydrate={alwaysHydrate}
          bentoHeroExists={!!bentoHero}
        />
      );
    })
  );

  const renderFixedSections = () => (
    <>
      <LayoutSection
        layoutComponent={quickLinks}
        index={quickLinksIdx}
        components={components}
        componentSelectorData={componentSelectorData}
      />
      <LayoutSection
        layoutComponent={marketingBanner}
        index={marketingBannerIdx}
        components={components}
        componentSelectorData={componentSelectorData}
      />
      <LayoutSection
        layoutComponent={heroCarousel}
        index={heroCarouselIdx}
        components={components}
        componentSelectorData={componentSelectorData}
      />
      <LayoutSection
        layoutComponent={bentoHero}
        index={bentoHeroIdx}
        components={components}
        componentSelectorData={componentSelectorData}
      />
    </>
  );

  if (layoutComponents.length === 0) return <></>;

  return (
    <div id={layoutType} className={style.layoutRoot}>
      {enableB2CPaddingAndGap ? (
        <div data-testid="layout-test" className={style.layoutGrid}>
          {renderFixedSections()}
          {componentOrderComponents()}
        </div>
      ) : (
        <>
          {renderFixedSections()}
          <div data-testid="layout-test" className={style.layoutGrid}>
            {componentOrderComponents()}
          </div>
        </>
      )}
      {(loading.server || loading.client) && LoadingLayout}
    </div>
  );
};

Layout.propTypes = {
  layoutType: oneOf(['themed-layout', 'default-layout']).isRequired,
  components: shape({
    [string]: element,
  }).isRequired,
  layoutComponents: arrayOf(shape),
  componentSelectorData: shape({
    variationIds: arrayOf(shape({ [string]: element })),
    renderDefault: bool,
    mboxes: arrayOf(string),
    innervateTags: arrayOf(shape({ [string]: element }))
  }).isRequired,
  loading: shape({
    server: bool,
    client: bool,
  }),
  LoadingLayout: element,
  alwaysHydrate: bool,
  enableB2CPaddingAndGap: bool
};